import { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { OnboardingLayout } from "@layouts/OnboardingLayout";
import { PrimaryButton } from "@shared/buttons";
import Logo from "@shared/Logo";

interface LocationState {
  email: string;
  code: string;
}

export const ChangePassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [togglePassword, setTogglePassword] = useState(false);
  const [toggleConfirmPassword, setToggleConfirmPassword] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const { email, code } = (location.state as LocationState) || {};

  const apiUrl = process.env["REACT_APP_API_BASE_URL"];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email || !code) {
      setError("Invalid request. Please restart the reset process.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await axios.post(
        `${apiUrl}/auth/mobile-reset-password`,
        {
          email,
          code,
          password,
          passwordConfirm: confirmPassword,
        }
      );

      if (response.data.success) {
        setSuccess("Password Reset Successfully");
        setTimeout(() => {
          setLoading(false);
          navigate("/sign-in", { replace: true });
        }, 2000);
      }
    } catch (error: any) {
      const errMsg =
        error.response?.data?.errors?.[0]?.message ||
        error.response?.data?.message ||
        "Failed to reset password";
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <OnboardingLayout>
      <div className="container">
        <Link className="text-black text-link" to="/">
          <Logo />
        </Link>

        <h4 className="fw-bolder">Change your password</h4>
        <p className="fs-14 mb-4">
          Remember to use a password you can remember but cannot be guessed
          easily.
        </p>

        <form className="mt-4 mt-md-3" onSubmit={handleSubmit}>
          <div className="col mb-4">
            <label className="form-label fw-semibold">New Password</label>
            <div className="input-group">
              <span className="input-group-text input-group-text-0">
                <i className="iconly-Lock icli fs-4"></i>
              </span>

              <input
                type={togglePassword ? "text" : "password"}
                className="form-control form-control-0 ps-3 py-3 border-start-0"
                placeholder="Enter your new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{ borderRight: "none" }}
              />

              <span className="input-group-text input-group-text-0">
                {togglePassword ? (
                  <i
                    className="iconly-Show icli fs-4"
                    onClick={() => setTogglePassword(!togglePassword)}
                    style={{ cursor: "pointer", color: "#05569f" }}
                  ></i>
                ) : (
                  <i
                    className="iconly-Hide icli fs-4"
                    onClick={() => setTogglePassword(!togglePassword)}
                    style={{ cursor: "pointer", color: "#05569f" }}
                  ></i>
                )}
              </span>
            </div>
          </div>

          <div className="col mb-4">
            <label className="form-label fw-semibold">
              Confirm New Password
            </label>
            <div className="input-group">
              <span className="input-group-text input-group-text-0">
                <i className="iconly-Lock icli fs-4"></i>
              </span>

              <input
                type={toggleConfirmPassword ? "text" : "password"}
                className="form-control form-control-0 ps-3 py-3 border-start-0"
                placeholder="Confirm your new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                style={{ borderRight: "none" }}
              />

              <span className="input-group-text input-group-text-0">
                {toggleConfirmPassword ? (
                  <i
                    className="iconly-Show icli fs-4"
                    onClick={() =>
                      setToggleConfirmPassword(!toggleConfirmPassword)
                    }
                    style={{ cursor: "pointer", color: "#05569f" }}
                  ></i>
                ) : (
                  <i
                    className="iconly-Hide icli fs-4"
                    onClick={() =>
                      setToggleConfirmPassword(!toggleConfirmPassword)
                    }
                    style={{ cursor: "pointer", color: "#05569f" }}
                  ></i>
                )}
              </span>
            </div>
          </div>

          <PrimaryButton
            type="primary"
            text="Set New Password"
            loading={loading}
          />
          {success && <span className="text-success col-12">{success}</span>}
          {error && <span className="text-danger col-12">{error}</span>}
        </form>
      </div>
    </OnboardingLayout>
  );
};
